import axios from "@/plugins/axios";
import utils from "@/stores/utils";
import { clearDraftForSession } from "@/composables/useStructuredDataDraft";

const POLL_INTERVAL_MS = 3000;
const DISMISSED_FAILURE_STORAGE_KEY = "sde_dismissed_failure_sessions";

function loadDismissedFailureSessionIds() {
    if (typeof window === "undefined") {
        return [];
    }
    try {
        const raw = window.sessionStorage.getItem(DISMISSED_FAILURE_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
        return [];
    }
}

function persistDismissedFailureSessionIds(sessionIds) {
    if (typeof window === "undefined") {
        return;
    }
    window.sessionStorage.setItem(
        DISMISSED_FAILURE_STORAGE_KEY,
        JSON.stringify(sessionIds.map(String))
    );
}

function normalizeExtractionContext(extraParams = {}) {
    const context = { ...(extraParams || {}) };
    if (context.provider != null) {
        context.provider =
            typeof context.provider === "object" ? context.provider.id : context.provider;
    }
    if (context.provider_id != null) {
        context.provider_id =
            typeof context.provider_id === "object"
                ? context.provider_id.id
                : context.provider_id;
    }
    return context;
}

const state = {
    activeSession: null,
    conflictSession: null,
    polling: false,
    pollTimer: null,
    editorOpen: false,
    dismissedFailureSessionIds: loadDismissedFailureSessionIds(),
};

const mutations = {
    setActiveSession(state, session) {
        state.activeSession = session;
    },
    setConflictSession(state, session) {
        state.conflictSession = session;
    },
    setPolling(state, polling) {
        state.polling = polling;
    },
    setPollTimer(state, timer) {
        state.pollTimer = timer;
    },
    setEditorOpen(state, open) {
        state.editorOpen = open;
    },
    markFailureDismissed(state, sessionId) {
        if (!sessionId) {
            return;
        }
        const normalizedId = String(sessionId);
        if (state.dismissedFailureSessionIds.includes(normalizedId)) {
            return;
        }
        state.dismissedFailureSessionIds = [
            ...state.dismissedFailureSessionIds,
            normalizedId,
        ];
        persistDismissedFailureSessionIds(state.dismissedFailureSessionIds);
    },
    clearFailureDismissed(state, sessionId) {
        if (!sessionId) {
            return;
        }
        const normalizedId = String(sessionId);
        state.dismissedFailureSessionIds = state.dismissedFailureSessionIds.filter(
            (id) => id !== normalizedId
        );
        persistDismissedFailureSessionIds(state.dismissedFailureSessionIds);
    },
    reset(state) {
        state.activeSession = null;
        state.conflictSession = null;
        state.polling = false;
        state.pollTimer = null;
        state.editorOpen = false;
        state.dismissedFailureSessionIds = [];
        persistDismissedFailureSessionIds([]);
    },
};

const actions = {
    async start({ dispatch, commit, rootState }, params) {
        const form = new FormData();
        form.append("file", params.file);
        form.append("sync_type", params.syncType);
        form.append("context", JSON.stringify(normalizeExtractionContext(params.extraParams)));
        if (params.savePdfOptIn) {
            form.append("save_pdf_to_provider", "true");
        }
        if (params.appendToSession) {
            form.append("append_to_session", params.appendToSession);
        }
        try {
            const { data } = await axios.post("/api/extractions/", form);
            commit("setActiveSession", data);
            commit("setConflictSession", null);
            dispatch("pollSession");
            return data;
        } catch (xhrError) {
            const error = utils.handleError(xhrError);
            if (error.code === 409 && error.details?.session) {
                commit("setConflictSession", error.details.session);
            }
            throw error;
        }
    },

    async pollSession({ state, commit, dispatch, rootState }) {
        if (state.polling) {
            return;
        }
        if (!rootState.session?.connected) {
            return;
        }
        commit("setPolling", true);

        const tick = async () => {
            try {
                const response = await axios.get("/api/extractions/current/", {
                    validateStatus: (status) => status === 200 || status === 204,
                });
                if (response.status === 204) {
                    commit("setActiveSession", null);
                    dispatch("stopPolling");
                    return;
                }
                commit("setActiveSession", response.data);
                if (
                    response.data.status === "running" ||
                    response.data.status === "pending"
                ) {
                    commit("setPollTimer", setTimeout(tick, POLL_INTERVAL_MS));
                } else {
                    if (
                        response.data.status === "failed" &&
                        (response.data.rows_extracted || 0) <= 0
                    ) {
                        commit("setActiveSession", null);
                    }
                    dispatch("stopPolling");
                }
            } catch {
                dispatch("stopPolling");
            }
        };
        await tick();
    },

    stopPolling({ state, commit }) {
        if (state.pollTimer) {
            clearTimeout(state.pollTimer);
            commit("setPollTimer", null);
        }
        commit("setPolling", false);
    },

    async cancel({ state, dispatch, commit, rootState }, payload = null) {
        const options =
            payload && typeof payload === "object" && !Array.isArray(payload)
                ? payload
                : {};
        const sessionId =
            options.sessionId ||
            (typeof payload === "string" ? payload : null) ||
            state.activeSession?.id;
        const keepDraft = Boolean(options.keepDraft);
        if (!sessionId) {
            return;
        }
        await axios.delete(`/api/extractions/${sessionId}/`);
        dispatch("stopPolling");
        commit("clearFailureDismissed", sessionId);
        if (!state.activeSession || String(state.activeSession.id) === String(sessionId)) {
            commit("setActiveSession", null);
        }
        const entityId = rootState.session?.entity?.id;
        const userId = rootState.session?.user?.id;
        if (!keepDraft && entityId && userId) {
            await clearDraftForSession(entityId, userId, sessionId);
        }
    },

    dismissFailedExtraction({ state, commit }, payload = null) {
        const sessionId =
            (payload && typeof payload === "object" ? payload.sessionId : null) ||
            (typeof payload === "string" ? payload : null) ||
            state.activeSession?.id;
        if (!sessionId) {
            return;
        }
        commit("markFailureDismissed", sessionId);
        if ((state.activeSession?.rows_extracted || 0) <= 0) {
            commit("setActiveSession", null);
        }
    },

    async loadSession({ state, commit, dispatch }, sessionId) {
        const { data } = await axios.get(`/api/extractions/${sessionId}/`);
        if (!state.activeSession || state.activeSession.id === sessionId) {
            commit("setActiveSession", data);
        }
        if (["pending", "running"].includes(data.status)) {
            dispatch("pollSession");
        }
        return data;
    },

    async finalize({ state, commit, dispatch, rootState }, { importSuccess = true } = {}) {
        if (!state.activeSession?.id) {
            return;
        }
        const sessionId = state.activeSession.id;
        await axios.post(`/api/extractions/${sessionId}/finalize/`, {
            import_success: importSuccess,
        });
        dispatch("stopPolling");
        commit("setActiveSession", null);
        commit("clearFailureDismissed", sessionId);
        const entityId = rootState.session?.entity?.id;
        const userId = rootState.session?.user?.id;
        if (importSuccess && entityId && userId) {
            await clearDraftForSession(entityId, userId, sessionId);
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
